import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from '../api/axiosClient';
import { useAuth } from './AuthContext';

// Portal Session Context
export const PortalSessionContext = createContext();

export const PortalSessionProvider = ({ children }) => {
  const { user } = useAuth();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPortalData = useCallback(async () => {
    if (!user) {
      setPortalData(null);
      setLoading(false);
      return;
    }

    // Only load portal data for clients
    if (user.role !== 'client') {
      setPortalData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/portal/me');
      setPortalData(response.data);
    } catch (err) {
      console.error('Failed to load portal data:', err);
      setError(err.response?.data?.error || 'Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updatePortalData = async (updates) => {
    try {
      const response = await axios.put('/portal/me', updates);
      setPortalData(prev => ({
        ...prev,
        ...response.data
      }));
      return { success: true };
    } catch (err) {
      console.error('Failed to update portal data:', err);
      return { 
        success: false, 
        error: err.response?.data?.error || 'Failed to update profile' 
      };
    }
  };

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  return (
    <PortalSessionContext.Provider value={{
      portalData,
      loading,
      error,
      loadPortalData,
      updatePortalData
    }}>
      {children}
    </PortalSessionContext.Provider>
  );
};

export const usePortalSession = () => {
  const context = useContext(PortalSessionContext);
  if (!context) {
    throw new Error('usePortalSession must be used within a PortalSessionProvider');
  }
  return context;
};

// Booking Draft Context
export const BookingDraftContext = createContext();

export const BookingDraftProvider = ({ children }) => {
  const [draft, setDraft] = useState(() => {
    // Load from sessionStorage on init
    const saved = sessionStorage.getItem('bookingDraft');
    return saved ? JSON.parse(saved) : { 
      barber_user_id: null, 
      service_id: null,
      date: null,
      slotHHMM: null,
      start_local: null,
      timezone: 'UTC',
      reschedule_booking_id: null
    };
  });

  const updateDraft = (updates) => {
    const newDraft = { ...draft, ...updates };
    setDraft(newDraft);
    // Save to sessionStorage
    sessionStorage.setItem('bookingDraft', JSON.stringify(newDraft));
  };

  const clearDraft = () => {
    setDraft({ 
      barber_user_id: null, 
      service_id: null,
      date: null,
      slotHHMM: null,
      start_local: null,
      timezone: 'UTC',
      reschedule_booking_id: null
    });
    sessionStorage.removeItem('bookingDraft');
  };

  const isComplete = draft.barber_user_id && draft.service_id;
  const isTimeSelected = draft.start_local && draft.date;

  return (
    <BookingDraftContext.Provider value={{
      draft,
      updateDraft,
      clearDraft,
      isComplete,
      isTimeSelected
    }}>
      {children}
    </BookingDraftContext.Provider>
  );
};

export const useBookingDraft = () => {
  const context = useContext(BookingDraftContext);
  if (!context) {
    throw new Error('useBookingDraft must be used within a BookingDraftProvider');
  }
  return context;
};
